import { Spacing } from '@persona-id/design-tokens';
import { uniqBy } from 'lodash/fp';
import { FC, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { generatePath, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { RedactedObjectPath } from 'dashboard/models/event';
import { Inquiry } from 'dashboard/models/inquiry';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Verification } from 'dashboard/models/verification';
import { TransactionWithRelatedData } from 'dashboard/routes/DashboardTransactionsShowRoute/util';
import Overview from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/Overview';
import RelatedAccounts from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedAccounts';
import RelatedCases from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedCases';
import RelatedInquiries from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedInquiries';
import RelatedListMatches from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedListMatches';
import RelatedReports from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedReports';
import RelatedTransactions from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedTransactions';
import RelatedVerifications from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/RelatedVerifications';
import Badge from 'design-system/components/Badge';
import TabBar, { Tab } from 'design-system/components/TabBar';

const PaddedTabBar = styled(TabBar)`
  margin-left: ${Spacing[5]};
  margin-right: ${Spacing[5]};
`;

const TabbedContent = styled.div`
  margin-top: ${Spacing[4]};
`;

type PossibleTabPath =
  | undefined
  | 'accounts'
  | 'cases'
  | 'inquiries'
  | 'lists'
  | 'reports'
  | 'transactions'
  | 'verifications';
const PAGE_PATH = '/dashboard/transactions/:transactionId';
const PAGE_PATH_WITH_TAB = `${PAGE_PATH}/:tab`;
const PATH_FOR_PAGE_AND_TAB = '/dashboard/transactions/:transactionId/:tab?';

interface TransactionDetailProps {
  cases: TransactionWithRelatedData['cases'];
  currentEnvironment: NonNullable<TransactionWithRelatedData['environment']>;
  redactedObjectPaths: RedactedObjectPath[];
  relatedAccounts: TransactionWithRelatedData['relatedAccounts'];
  relatedCases: TransactionWithRelatedData['relatedCases'];
  relatedInquiries: TransactionWithRelatedData['relatedInquiries'];
  relatedReports: TransactionWithRelatedData['relatedReports'];
  relatedTransactions: TransactionWithRelatedData['relatedTransactions'];
  relatedVerifications: TransactionWithRelatedData['relatedVerifications'];
  relations: TransactionWithRelatedData['relations'];
  transaction: NonNullable<TransactionWithRelatedData['transaction']>;
  transactionTags: TransactionWithRelatedData['tags'];
  lists: Record<string, List>;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
}

const TransactionDetail: FC<TransactionDetailProps> = ({
  cases,
  currentEnvironment,
  redactedObjectPaths,
  relatedAccounts,
  relatedCases,
  relatedInquiries,
  relatedReports,
  relatedTransactions,
  relatedVerifications,
  relations,
  transaction,
  transactionTags,
  lists,
  listItems,
  listMatches,
  handleUpdateTags,
  handleFlagInquiryIssues,
  handleFlagVerificationIssues,
}) => {
  const navigate = useNavigate();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { params: matchedParams } = useRouteMatch<{ transactionId: string; tab: PossibleTabPath }>(
    PATH_FOR_PAGE_AND_TAB,
  );

  const possibleTabs = useMemo(() => {
    // [path for :tab, label, count]
    const tabs = (
      [
        [undefined, 'Overview', 0],
        ['accounts', 'Accounts', relatedAccounts.length],
        ['cases', 'Cases', relatedCases.length],
        ['inquiries', 'Inquiries', relatedInquiries.length],
        ['reports', 'Reports', relatedReports.length],
        ['transactions', 'Transactions', relatedTransactions.length],
        ['verifications', 'Verifications', relatedVerifications.length],
      ] as const
    )
      .filter(([_path, label, numOfObjects]) => numOfObjects > 0 || label === 'Overview')
      .map(([tabPath, label, numOfObjects]) => ({
        tabPath,
        label,
        numOfObjects,
      }));

    return tabs.length === 1 ? [] : tabs;
  }, [
    relatedAccounts,
    relatedCases,
    relatedInquiries,
    relatedReports,
    relatedTransactions,
    relatedVerifications,
  ]);

  const selectedTabContent = useMemo(() => {
    switch (matchedParams.tab) {
      case 'accounts':
        return <RelatedAccounts accounts={relatedAccounts} />;
      case 'cases':
        return <RelatedCases cases={uniqBy('id', [...relatedCases, ...cases])} />;
      case 'inquiries':
        return (
          <RelatedInquiries
            inquiries={relatedInquiries}
            handleFlagInquiryIssues={handleFlagInquiryIssues}
          />
        );
      case 'lists':
        return <RelatedListMatches listMatches={[]} />;
      case 'reports':
        return <RelatedReports reports={relatedReports} />;
      case 'transactions':
        return <RelatedTransactions transactions={relatedTransactions} />;
      case 'verifications':
        return (
          <RelatedVerifications
            verifications={relatedVerifications}
            handleFlagVerificationIssues={handleFlagVerificationIssues}
          />
        );
      default:
        return (
          <Overview
            currentEnvironment={currentEnvironment}
            redactedObjectPaths={redactedObjectPaths}
            relatedAccounts={relatedAccounts}
            relatedCases={uniqBy('id', [...relatedCases, ...cases])}
            relatedInquiries={relatedInquiries}
            relatedReports={relatedReports}
            relatedTransactions={relatedTransactions}
            relatedVerifications={relatedVerifications}
            relations={relations}
            transaction={transaction}
            transactionTags={transactionTags}
            lists={lists}
            listItems={listItems}
            listMatches={listMatches}
            handleUpdateTags={handleUpdateTags}
          />
        );
    }
  }, [
    cases,
    matchedParams,
    currentEnvironment,
    transaction,
    transactionTags,
    handleUpdateTags,
    redactedObjectPaths,
    relatedAccounts,
    relatedCases,
    relatedInquiries,
    relatedReports,
    relatedTransactions,
    relatedVerifications,
    relations,
  ]);

  return (
    <>
      {possibleTabs.length > 0 && (
        <PaddedTabBar>
          {possibleTabs.map(({ tabPath, label, numOfObjects }) => (
            <Tab
              badge={numOfObjects > 0 ? <Badge>{numOfObjects}</Badge> : <></>}
              id={`transaction-detail__tab-${tabPath}`}
              key={label}
              selected={matchedParams.tab === tabPath}
              onClick={() => {
                if (tabPath) {
                  return navigate(
                    generatePath(PAGE_PATH_WITH_TAB, {
                      transactionId: matchedParams.transactionId,
                      tab: tabPath,
                    }),
                    { replace: true },
                  );
                } else {
                  return navigate(
                    generatePath(PAGE_PATH, {
                      transactionId: matchedParams.transactionId,
                    }),
                    { replace: true },
                  );
                }
              }}
            >
              {label}
            </Tab>
          ))}
        </PaddedTabBar>
      )}
      <TabbedContent>{selectedTabContent}</TabbedContent>
    </>
  );
};

export default TransactionDetail;
